import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  StickyNote,
  Send,
  Trash2,
  Loader2,
  User,
  ShieldCheck,
  HeartHandshake,
} from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { el } from "date-fns/locale";

export interface LeadNote {
  id: string;
  leadId: string;
  authorType: "admin" | "partner";
  authorId: string | null;
  authorName: string | null;
  note: string;
  createdAt: string;
}

interface LeadNotesPanelProps {
  leadId: string;
  /** "admin" when rendered in the admin portal, "partner" in the partner/guest view */
  authorType: "admin" | "partner";
  /** Optional display name for the note author (e.g. partner name or admin username) */
  authorName?: string;
  /** Optional id (admin id or partner id) — stored for audit */
  authorId?: string;
  /** Allow delete: admins always true; partners only their own notes */
  canDelete?: boolean;
  /** Bearer proof scoped to this lead, used by direct SMS/email links. */
  accessToken?: string;
}

const authHeaders = (accessToken?: string): Record<string, string> => {
  const csrf = sessionStorage.getItem("csrf_token") ?? "";
  return {
    "Content-Type": "application/json",
    "X-CSRF-Token": csrf,
    ...(accessToken ? { "X-Lead-Access-Token": accessToken } : {}),
  };
};

const formatWhen = (iso: string) => {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: el });
  } catch {
    return iso;
  }
};

const LeadNotesPanel: React.FC<LeadNotesPanelProps> = ({
  leadId,
  authorType,
  authorName,
  authorId,
  canDelete = true,
  accessToken,
}) => {
  const { toast } = useToast();
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/lead-notes.php?leadId=${encodeURIComponent(leadId)}`, {
        method: "GET",
        headers: authHeaders(accessToken),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Αποτυχία φόρτωσης");
      setNotes(Array.isArray(data.data) ? data.data : []);
    } catch (err: any) {
      toast({ title: "Σφάλμα", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) loadNotes();
  }, [leadId, accessToken]);

  const handleSubmit = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    if (trimmed.length > 2000) {
      toast({ title: "Πολύ μεγάλο", description: "Max 2000 χαρακτήρες.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/lead-notes.php", {
        method: "POST",
        headers: authHeaders(accessToken),
        credentials: "include",
        body: JSON.stringify({
          leadId,
          note: trimmed,
          authorType,
          authorName: authorName ?? null,
          authorId: authorId ?? null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Αποτυχία αποθήκευσης");
      setNotes(prev => [data.data, ...prev]);
      setNewNote("");
      toast({ title: "Η σημείωση αποθηκεύτηκε ✅" });
    } catch (err: any) {
      toast({ title: "Σφάλμα", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!confirm("Διαγραφή της σημείωσης;")) return;
    try {
      const res = await fetch(`/api/lead-notes.php?noteId=${encodeURIComponent(noteId)}`, {
        method: "DELETE",
        headers: authHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Αποτυχία διαγραφής");
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast({ title: "Η σημείωση διαγράφηκε" });
    } catch (err: any) {
      toast({ title: "Σφάλμα", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-amber-500" />
          Σημειώσεις & Ιστορικό
          {notes.length > 0 && (
            <Badge variant="secondary" className="ml-auto">{notes.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* New note form */}
        <div className="space-y-2">
          <Textarea
            placeholder="Γράψτε μια σημείωση… π.χ. «Μίλησα στις 14:30, ζήτησε προσφορά την Παρασκευή»"
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            rows={3}
            maxLength={2000}
            className="resize-none border-slate-200"
            disabled={submitting}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{newNote.length}/2000</span>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !newNote.trim()}
              size="sm"
              className="gap-2"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Προσθήκη
            </Button>
          </div>
        </div>

        {/* Notes list */}
        <div className="border-t border-slate-100 pt-3">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              Δεν υπάρχουν σημειώσεις ακόμα
            </p>
          ) : (
            <ul className="space-y-3 max-h-96 overflow-y-auto">
              {notes.map(n => {
                const isAdmin = n.authorType === "admin";
                return (
                  <li
                    key={n.id}
                    className={`rounded-lg border p-3 transition-colors ${
                      isAdmin
                        ? "border-blue-100 bg-blue-50/50"
                        : "border-amber-100 bg-amber-50/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`p-1 rounded ${isAdmin ? "bg-blue-100" : "bg-amber-100"}`}>
                          {isAdmin ? (
                            <ShieldCheck className="h-3 w-3 text-blue-600" />
                          ) : (
                            <HeartHandshake className="h-3 w-3 text-amber-600" />
                          )}
                        </div>
                        <span className="text-xs font-semibold text-slate-700">
                          {n.authorName ?? (isAdmin ? "Διαχειριστής" : "Συνεργάτης")}
                        </span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                          isAdmin ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {isAdmin ? "Admin" : "Partner"}
                        </span>
                      </div>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(n.id)}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                          title="Διαγραφή"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{n.note}</p>
                    <p className="text-xs text-slate-400 mt-1.5">{formatWhen(n.createdAt)}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadNotesPanel;
